import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AttachFileIcon,
  ClaimScoreIcon,
  ClipboardIcon,
  DashboardIcon,
  ElementWithBadge,
  Flags,
  KeyOutlinedIcon,
  NavItem,
  SettingsIcon,
  SideNavDivider,
  SupervisorAccountIcon,
  useFlag,
} from 'ui';
import { NavItemProps } from 'ui/components/NavItem/NavItem';
import { RoleLevel } from '@context/UserOrgCase.types';
import { useUserOrgCase } from '@context/UserOrgCaseContext';
import { getOrgAndCaseId } from '@utils/getOrgAndCaseId';
import { PATHS } from '@utils/paths';
import { UserRole } from 'services/ClaimscoreApiService/shared/types';
import { CLAIMSCORE_SETTINGS_NAV_BADGE } from './SideNavbar.testIds';

const DASHBOARD: NavItemProps = {
  label: 'Dashboard',
  icon: <DashboardIcon />,
  to: `/dashboard`,
};

const PERMISSIONS: NavItemProps = {
  label: 'Permissions',
  icon: <SupervisorAccountIcon />,
  to: `/permissions/organization`,
  activeUrl: `/permissions/`,
};

const REPORTS_ITEM: NavItemProps = {
  label: 'Reports',
  icon: <ClipboardIcon />,
  to: PATHS.PRIVATE_BASE.scheduledReports,
};

const CLAIMSCORE_SETTINGS: NavItemProps = {
  label: 'ClaimScore Settings',
  icon: <ClaimScoreIcon />,
  to: `${PATHS.PRIVATE_BASE.claimscoreSettings}`,
  activeUrl: `${PATHS.PRIVATE_BASE.claimscoreSettings}`,
};

const CASE_SETTINGS: NavItemProps = {
  label: 'Case Settings',
  icon: <SettingsIcon />,
  to: `${PATHS.PRIVATE_BASE.caseSettings}`,
  activeUrl: `${PATHS.PRIVATE_BASE.caseSettings}/`,
};

const CASE_SETUP: NavItemProps = {
  label: 'Case Setup',
  icon: <SettingsIcon />,
  to: `${PATHS.PRIVATE_BASE.caseSetup}`,
  activeUrl: `${PATHS.PRIVATE_BASE.caseSetup}`,
};

const CLAIM_FILES: NavItemProps = {
  label: 'Claim Files',
  icon: <AttachFileIcon />,
  to: `${PATHS.PRIVATE_BASE.claimFiles}`,
};

const DEVELOPER_TOOLS: NavItemProps = {
  label: 'Developer Tools',
  icon: <KeyOutlinedIcon />,
  to: '/developer-tools/api-keys',
};

function selectedCaseHasClaimSubmissionWindow(
  selectedCase:
    | {
        readonly claimSubmissionStartDate?: string;
        readonly claimSubmissionEndDate?: string;
      }
    | undefined,
): boolean {
  return Boolean(
    selectedCase?.claimSubmissionStartDate &&
      selectedCase?.claimSubmissionEndDate,
  );
}

export function useMainSideNavbarNavItems() {
  const { search } = useLocation();
  const {
    getUserRole,
    isCurrentCaseAdmin,
    isCurrentOrgAdmin,
    selectedCase,
    isLoading,
  } = useUserOrgCase();

  const isCaseSettingsEnabled = useFlag(Flags.caseSettings);
  const isUploadClaimFilesEnabled = useFlag(Flags.uploadClaimsFile);
  const isClaimscoreSettingsEnabled = useFlag(Flags.claimscoreSettings);

  const isDeveloper =
    getUserRole(RoleLevel.organization) === UserRole.Developer;

  const orgCaseQuery = getOrgAndCaseId(search);

  return useMemo(() => {
    const caseNavigationItem = isClaimscoreSettingsEnabled
      ? CASE_SETUP
      : CASE_SETTINGS;
    const showWarning =
      !isLoading && !selectedCaseHasClaimSubmissionWindow(selectedCase);

    const claimscoreSettingsIcon = showWarning ? (
      <ElementWithBadge
        badgeColor="warning"
        counter={0}
        data-testid={CLAIMSCORE_SETTINGS_NAV_BADGE}
        element={<ClaimScoreIcon />}
        name="ClaimScore Settings"
        size="small"
      />
    ) : (
      <ClaimScoreIcon />
    );

    if (isClaimscoreSettingsEnabled) {
      return (
        <>
          <NavItem
            {...DASHBOARD}
            kind="dark"
            to={`${DASHBOARD.to}${orgCaseQuery}`}
          />
          <NavItem
            {...REPORTS_ITEM}
            kind="dark"
            to={`${REPORTS_ITEM.to}${orgCaseQuery}`}
            visible={Boolean(isCurrentCaseAdmin)}
          />
          <NavItem
            {...CLAIM_FILES}
            kind="dark"
            to={`${CLAIM_FILES.to}${orgCaseQuery}`}
            visible={Boolean(isUploadClaimFilesEnabled && isCurrentCaseAdmin)}
          />
          {isCurrentCaseAdmin ? (
            <>
              <SideNavDivider light={false} topMargin />
              <NavItem
                {...CLAIMSCORE_SETTINGS}
                bigMargins
                icon={claimscoreSettingsIcon}
                kind="dark"
                to={`${CLAIMSCORE_SETTINGS.to}${orgCaseQuery}`}
              />
            </>
          ) : null}
          <SideNavDivider bottomMargin light={false} />
          <NavItem
            {...PERMISSIONS}
            kind="dark"
            to={`${PERMISSIONS.to}${orgCaseQuery}`}
            visible={Boolean(isCurrentOrgAdmin || isCurrentCaseAdmin)}
          />
          <NavItem
            {...DEVELOPER_TOOLS}
            kind="dark"
            to={`${DEVELOPER_TOOLS.to}${orgCaseQuery}`}
            visible={Boolean(isCurrentOrgAdmin || isDeveloper)}
          />
          <NavItem
            {...caseNavigationItem}
            kind="dark"
            to={`${caseNavigationItem.to}${orgCaseQuery}`}
            visible={Boolean(isCaseSettingsEnabled && isCurrentCaseAdmin)}
          />
        </>
      );
    }

    return (
      <>
        <NavItem
          {...DASHBOARD}
          kind="dark"
          to={`${DASHBOARD.to}${orgCaseQuery}`}
        />
        <NavItem
          {...PERMISSIONS}
          kind="dark"
          to={`${PERMISSIONS.to}${orgCaseQuery}`}
          visible={Boolean(isCurrentOrgAdmin || isCurrentCaseAdmin)}
        />
        <NavItem
          {...REPORTS_ITEM}
          kind="dark"
          to={`${REPORTS_ITEM.to}${orgCaseQuery}`}
          visible={Boolean(isCurrentCaseAdmin)}
        />
        <NavItem
          {...caseNavigationItem}
          kind="dark"
          to={`${caseNavigationItem.to}${orgCaseQuery}`}
          visible={Boolean(isCaseSettingsEnabled && isCurrentCaseAdmin)}
        />
        <NavItem
          {...CLAIM_FILES}
          kind="dark"
          to={`${CLAIM_FILES.to}${orgCaseQuery}`}
          visible={Boolean(isUploadClaimFilesEnabled && isCurrentCaseAdmin)}
        />
        <NavItem
          {...DEVELOPER_TOOLS}
          kind="dark"
          to={`${DEVELOPER_TOOLS.to}${orgCaseQuery}`}
          visible={Boolean(isCurrentOrgAdmin || isDeveloper)}
        />
      </>
    );
  }, [
    isLoading,
    selectedCase,
    isClaimscoreSettingsEnabled,
    orgCaseQuery,
    isCurrentOrgAdmin,
    isCurrentCaseAdmin,
    isCaseSettingsEnabled,
    isUploadClaimFilesEnabled,
    isDeveloper,
  ]);
}
